import { useEffect, useRef, useState } from "react";
import PolicyModal from "./PolicyModal";
import GuaranteeSeal from "./GuaranteeSeal";
import { asset } from "../../assetUrl";
import "./Template7.css";
import { BRAND_DEFAULT_SVG } from "../../brandDefaultLogo";

/* ──────────────────────────────────────────────────────────────────────────
   Template 7 — "Spotlight Noir" (premium dark)
   A motion-rich dark sales page: a cursor-following radial spotlight, animated
   gradient text, shimmer/magnetic buttons, 3D-tilt hover cards and count-up
   stats. Pure CSS animation + IntersectionObserver (no extra deps). Theming is
   data-driven via the wrapper's --cl-* vars; scoped under `.clp7-root`. Shares
   GuaranteeSeal + PolicyModal with the other templates.
   ────────────────────────────────────────────────────────────────────────── */

// Course-landing pages are light-only (no dark: classes anywhere in this
// folder), so the light placeholder is the right one. The old default was
// FlexiLogo.svg — OUR mark, on a CUSTOMER's sales page, which contradicts the
// rule stated on BrandLogo — and it was a 100 KB "SVG" that turned out to be a
// single <rect> filled with a base64 PNG, so it could not be recoloured either.
const DEFAULT_LOGO = BRAND_DEFAULT_SVG;
const DEFAULT_COURSE_IMAGE = asset("/images/course-default.svg");

const I = {
  cap: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8.211 2.047a.5.5 0 0 0-.422 0l-7.5 3.5a.5.5 0 0 0 .025.917l7.5 3a.5.5 0 0 0 .372 0L14 7.14V13a1 1 0 0 0-1 1v2h3v-2a1 1 0 0 0-1-1V6.739l.686-.275a.5.5 0 0 0 .025-.917l-7.5-3.5Z" />
      <path d="M4.176 9.032a.5.5 0 0 0-.656.327l-.5 1.7a.5.5 0 0 0 .294.605l4.5 1.8a.5.5 0 0 0 .372 0l4.5-1.8a.5.5 0 0 0 .294-.605l-.5-1.7a.5.5 0 0 0-.656-.327L8 10.466 4.176 9.032Z" />
    </svg>
  ),
  play: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
      <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z" />
    </svg>
  ),
  playFill: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" />
    </svg>
  ),
  book: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z" />
    </svg>
  ),
  fileText: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z" />
      <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
    </svg>
  ),
  playCircle: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z" />
    </svg>
  ),
  check: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ),
  dot: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
    </svg>
  ),
  chevron: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
    </svg>
  ),
  plus: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
    </svg>
  ),
  shield: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.3 8.531 1.1 8 1.1c-.531 0-1.552.2-2.662.49z" />
      <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ),
  user: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm-9 8c0 1 1 1 1 1h10s1 0 1-1-1-4-6-4-6 3-6 4z" />
    </svg>
  ),
  arrow: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
    </svg>
  ),
  x: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z" />
    </svg>
  ),
  youtube: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
    </svg>
  ),
  linkedin: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
    </svg>
  ),
  instagram: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
    </svg>
  ),
  facebook: (p) => (
    <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em" {...p}>
      <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
    </svg>
  ),
};

const initials = (name) =>
  String(name || "").split(/\s+/).filter(Boolean).map((w) => w[0].toUpperCase()).slice(0, 2).join("");
const starStr = (rating, max = 5) => {
  const r = Math.round(Number(rating) || 0);
  return Array.from({ length: max }, (_, i) => (i < r ? "★" : "☆")).join("");
};

/* Count-up number that animates the first time it scrolls into view. */
function CountUp({ to, decimals = 0, dur = 1500 }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || !to) return;
    let raf = 0;
    let settle = 0;
    let done = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !done) {
            done = true;
            const t0 = performance.now();
            const step = (now) => {
              const p = Math.min(1, (now - t0) / dur);
              const eased = 1 - Math.pow(1 - p, 3);
              setVal(to * eased);
              if (p < 1) raf = requestAnimationFrame(step);
            };
            raf = requestAnimationFrame(step);
            // Safety net: guarantee the final value lands even where rAF is
            // throttled (background tabs, reduced motion, headless capture).
            settle = setTimeout(() => setVal(to), dur + 60);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(settle);
    };
  }, [to, dur]);
  return <span ref={ref}>{decimals ? val.toFixed(decimals) : Math.round(val)}</span>;
}

/* Pointer 3D-tilt handlers — pure (operate on e.currentTarget only). */
const tilt = (e) => {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const px = (e.clientX - r.left) / r.width - 0.5;
  const py = (e.clientY - r.top) / r.height - 0.5;
  el.style.transform = `perspective(800px) rotateY(${px * 7}deg) rotateX(${-py * 7}deg) translateY(-4px)`;
};
const untilt = (e) => {
  e.currentTarget.style.transform = "";
};

/* Point cards (skills / includes / prerequisites). Module-level so its identity
   stays stable across re-renders and React never remounts the subtree. */
function Pts({ list, icon }) {
  return (
    <div className="g7-grid">
      {list.map((s, i) => (
        <div className="g7-card" onMouseMove={tilt} onMouseLeave={untilt} key={i}>
          <span className="g7-ic">{icon}</span>
          <p>{s.pts || ""}</p>
        </div>
      ))}
    </div>
  );
}

export default function Template7({ data }) {
  const {
    course = {},
    logo = null,
    header = {},
    skill = {},
    course_include = {},
    prerequisites = {},
    testimonial = {},
    money_back = {},
    faq = {},
    social = {},
    policies = {},
    labels = {},
    video = {},
    video_embed = null,
    modules = [],
    lessonsByModule = {},
    moduleCount = 0,
    lessonCount = 0,
    instructor = null,
    rating = 0,
    members = [],
    memberCount = 0,
    enrollText = "Enroll now",
    enrollLink = "#",
  } = data || {};

  const lbl = (k, d) => (String(labels?.[k] || "").trim() !== "" ? labels[k] : d);
  const courseName = course.name || course.title || "";
  const heroTitle = String(header.headline || "").trim() !== "" ? header.headline : courseName;
  const heroSub = String(header.sub_headline || "").trim() !== "" ? header.sub_headline : course.short_description || "";
  const logoImg = String(logo || "").trim() !== "" ? logo : DEFAULT_LOGO;
  const coverImg = course.funnel_page_image || DEFAULT_COURSE_IMAGE;
  const showCurriculum = course_include.status === undefined ? true : !!course_include.status;
  const headerVisible = header.status === undefined || header.status;
  const lessonsFor = (cid) => lessonsByModule?.[String(cid)] || [];

  const mbStatus = money_back?.status === undefined ? 1 : money_back.status;
  const mbHeading = String(money_back.heading || "").trim() || "30-Day Money-Back Guarantee";
  const mbDesc =
    String(money_back.description || "").trim() ||
    "We're confident this course will help you reach your goals. If it isn't the right fit within 30 days, reach out and we'll refund you in full — no questions asked.";

  const [openModule, setOpenModule] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [openPolicy, setOpenPolicy] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const hasPrivacy = String(policies?.privacy || "").trim() !== "";
  const hasTerms = String(policies?.terms || "").trim() !== "";

  const isOther = (video.video_type || "") === "other";
  const autoplaySrc = (() => {
    if (!video_embed) return null;
    const base = video_embed + (video_embed.includes("?") ? "&" : "?") + "enablejsapi=1";
    return base.includes("autoplay=1") ? base : base + "&autoplay=1";
  })();

  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    root.querySelectorAll(".rv").forEach((el) => io.observe(el));
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [data]);

  // Cursor-following spotlight (sets CSS vars consumed by .g7-spot).
  const onMove = (e) => {
    const el = rootRef.current;
    if (!el) return;
    el.style.setProperty("--mx", `${e.clientX}px`);
    el.style.setProperty("--my", `${e.clientY}px`);
  };

  // 3D tilt on a card element.
  const goTo = (e, id) => {
    const t = rootRef.current?.querySelector(id);
    setMenuOpen(false);
    if (t) {
      e.preventDefault();
      t.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const heroBg = header.header_bg_image
    ? { backgroundImage: `url('${header.header_bg_image}')`, backgroundSize: "cover", backgroundPosition: "center" }
    : undefined;
  const guarBg = money_back.money_back_bg_image
    ? { backgroundImage: `url('${money_back.money_back_bg_image}')`, backgroundSize: "cover", backgroundPosition: "center" }
    : undefined;

  const navLinks = (
    <>
      {moduleCount && showCurriculum ? <a href="#curriculum" onClick={(e) => goTo(e, "#curriculum")}>{lbl("curriculum", "Curriculum")}</a> : null}
      {instructor && instructor.name ? <a href="#instructor" onClick={(e) => goTo(e, "#instructor")}>{lbl("instructor", "Instructor")}</a> : null}
      {testimonial?.status && testimonial?.list?.length ? <a href="#reviews" onClick={(e) => goTo(e, "#reviews")}>{lbl("reviews", "Reviews")}</a> : null}
      {faq?.status && faq?.list?.length ? <a href="#faq" onClick={(e) => goTo(e, "#faq")}>FAQ</a> : null}
    </>
  );

  return (
    <div className="clp7-root" ref={rootRef} onMouseMove={onMove}>
      <div className="g7-spot" aria-hidden="true" />
      <div className="g7-grid-bg" aria-hidden="true" />

      {/* NAV */}
      <nav className={`g7-nav${scrolled ? " scr" : ""}`}>
        <div className="g7-wrap g7-nav-in">
          <a href="#top" className="g7-logo" onClick={(e) => goTo(e, "#top")}><img src={logoImg} alt="Logo" /></a>
          <div className="g7-links">{navLinks}</div>
          <div className="g7-nav-cta">
            <a href={enrollLink} className="g7-btn g7-btn-p"><I.cap /> {enrollText}</a>
            <button className={`g7-ham${menuOpen ? " on" : ""}`} type="button" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu"><span /><span /><span /></button>
          </div>
        </div>
        <div className={`g7-mnav${menuOpen ? " on" : ""}`}>
          {navLinks}
          <a href={enrollLink} className="g7-btn g7-btn-p" onClick={() => setMenuOpen(false)}><I.cap /> {enrollText}</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="g7-hero" id="top" style={heroBg}>
        <div className="g7-wrap g7-hero-grid">
          <div className="g7-hero-l">
            <div className="g7-pill rv"><span className="g7-dot" /> {lbl("premium_course", "Premium course")}</div>
            <h1 className="g7-title rv"><span className="g7-grad">{heroTitle}</span></h1>
            {heroSub ? <p className="g7-sub rv">{heroSub}</p> : null}
            <div className="g7-actions rv">
              <a href={enrollLink} className="g7-btn g7-btn-p g7-btn-lg g7-shimmer"><I.cap /> {enrollText}</a>
              {moduleCount && showCurriculum ? (
                <a href="#curriculum" className="g7-btn g7-btn-ghost g7-btn-lg" onClick={(e) => goTo(e, "#curriculum")}><I.play /> {lbl("preview_course", "Preview course")}</a>
              ) : null}
            </div>
            <div className="g7-stats rv">
              <div><strong><CountUp to={moduleCount} /></strong><span>{lbl("modules", "Modules")}</span></div>
              <i />
              <div><strong><CountUp to={lessonCount} /></strong><span>{lbl("lessons", "Lessons")}</span></div>
              {rating > 0 ? (<><i /><div><strong><CountUp to={Number(rating)} decimals={1} /></strong><span>{lbl("rating", "Rating")}</span></div></>) : null}
              {memberCount > 0 ? (<><i /><div><strong><CountUp to={memberCount} />+</strong><span>{lbl("members", "Members")}</span></div></>) : null}
            </div>
          </div>

          {headerVisible ? (
            <div className="g7-hero-r rv">
              <div className="g7-frame" onMouseMove={tilt} onMouseLeave={untilt}>
                <div className="g7-frame-bar"><span /><span /><span /></div>
                <div className="g7-media">
                  {video_embed && videoOn ? (
                    isOther ? (
                      <video controls autoPlay><source src={video_embed} type="video/mp4" /></video>
                    ) : (
                      <iframe title="course-video" src={autoplaySrc} frameBorder="0" allow="autoplay; fullscreen" allowFullScreen />
                    )
                  ) : (
                    <>
                      <img src={video.image || coverImg} alt="" />
                      {video_embed ? <button className="g7-playbtn" type="button" onClick={() => setVideoOn(true)} aria-label="Play"><I.playFill /></button> : null}
                    </>
                  )}
                </div>
                {modules.length ? (
                  <div className="g7-mini">
                    {modules.slice(0, 3).map((m) => (
                      <div className="g7-mini-row" key={m.course_id}>
                        <span className="g7-mini-ic"><I.book /></span>
                        <span className="g7-mini-t">{m.course_name}</span>
                        <span className="g7-mini-m">{lessonsFor(m.course_id).length} {lbl("lessons", "lessons")}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </header>

      {/* WHAT YOU'LL LEARN */}
      {skill?.status && skill?.list?.length ? (
        <section className="g7-sec" id="learn">
          <div className="g7-wrap">
            <div className="g7-head rv"><span className="g7-eyebrow">{lbl("what_youll_learn", "What you'll learn")}</span><h2>{skill.heading || "What you'll learn"}</h2></div>
            <div className="rv"><Pts list={skill.list} icon={<I.check />} /></div>
            {skill.long_desc ? <p className="g7-longdesc rv" dangerouslySetInnerHTML={{ __html: String(skill.long_desc).replace(/\n/g, "<br>") }} /> : null}
          </div>
        </section>
      ) : null}

      {/* COURSE INCLUDES */}
      {course_include?.status && course_include?.list?.length ? (
        <section className="g7-sec" id="includes">
          <div className="g7-wrap">
            <div className="g7-head rv"><span className="g7-eyebrow">{lbl("what_inside_course", "What's inside")}</span><h2>{course_include.heading || "This course includes"}</h2></div>
            <div className="rv"><Pts list={course_include.list} icon={<I.check />} /></div>
          </div>
        </section>
      ) : null}

      {/* PREREQUISITES */}
      {prerequisites?.status && prerequisites?.list?.length ? (
        <section className="g7-sec" id="requirements">
          <div className="g7-wrap">
            <div className="g7-head rv"><span className="g7-eyebrow">{lbl("prerequisites", "Prerequisites")}</span><h2>{prerequisites.heading || "Before you start"}</h2></div>
            <div className="rv"><Pts list={prerequisites.list} icon={<I.dot />} /></div>
          </div>
        </section>
      ) : null}

      {/* CURRICULUM */}
      {moduleCount && showCurriculum ? (
        <section className="g7-sec" id="curriculum">
          <div className="g7-wrap">
            <div className="g7-head rv"><span className="g7-eyebrow">{lbl("curriculum", "Curriculum")}</span><h2>{lbl("inside_course", "What's inside the course")}</h2><p className="g7-head-meta">{moduleCount} {lbl("modules", "modules")} · {lessonCount} {lbl("lessons", "lessons")}</p></div>
            <div className="g7-acc rv">
              {modules.map((m, idx) => {
                const mls = lessonsFor(m.course_id);
                const open = openModule === idx;
                return (
                  <div className={`g7-ai${open ? " open" : ""}`} key={m.course_id}>
                    <button className="g7-ah" type="button" onClick={() => setOpenModule(open ? -1 : idx)} aria-expanded={open}>
                      <span className="g7-anum">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="g7-at">{m.course_name}</span>
                      <span className="g7-am">{mls.length} {lbl("lessons", "lessons")}</span>
                      <I.chevron className="g7-achev" />
                    </button>
                    {open ? (
                      <div className="g7-ab">
                        {mls.length ? mls.map((c) => (
                          <div className="g7-lrow" key={c.lesson_id}>
                            {Number(c.lesson_type || 0) === 0 ? <I.fileText /> : <I.playCircle />}
                            <span>{String(c.lesson_name || "Lesson").slice(0, 90)}</span>
                          </div>
                        )) : <div className="g7-lrow g7-muted">{lbl("no_lesson_found", "No lessons found")}</div>}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* INSTRUCTOR */}
      {instructor && instructor.name ? (
        <section className="g7-sec" id="instructor">
          <div className="g7-wrap">
            <div className="g7-inst rv">
              <div className="g7-inst-av">{instructor.profile_image ? <img src={instructor.profile_image} alt="Instructor" /> : <span>{initials(instructor.name) || <I.user />}</span>}</div>
              <div className="g7-inst-body">
                <span className="g7-eyebrow">{lbl("instructor", "Instructor")}</span>
                <h3>{instructor.name}</h3>
                {instructor.location ? <div className="g7-inst-role">{instructor.location}</div> : null}
                <p>{instructor.short_description || ""}</p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* TESTIMONIALS */}
      {testimonial?.status && testimonial?.list?.length ? (
        <section className="g7-sec" id="reviews">
          <div className="g7-wrap">
            <div className="g7-head rv"><span className="g7-eyebrow">{lbl("testimonials", "Testimonials")}</span><h2>{testimonial.heading || "What students are saying"}</h2></div>
            <div className="g7-rev-grid rv">
              {testimonial.list.map((t, i) => (
                <figure className="g7-rev" onMouseMove={tilt} onMouseLeave={untilt} key={i}>
                  <div className="g7-rev-stars">{starStr(t.rating ?? 5)}</div>
                  <blockquote>"{t.review || ""}"</blockquote>
                  <figcaption>
                    <span className="g7-rev-av">{t.name ? initials(t.name) : "NA"}</span>
                    <span><strong>{t.name || "Anonymous"}</strong><em>{t.designation || "Student"}</em></span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* GUARANTEE */}
      {mbStatus == 1 ? (
        <section className="g7-sec" style={guarBg}>
          <div className="g7-wrap">
            <div className="g7-guar rv">
              <div className="g7-guar-body">
                <span className="g7-eyebrow">{lbl("guarantee", "Risk-free")}</span>
                <h2>{mbHeading}</h2>
                <div className="g7-guar-txt" dangerouslySetInnerHTML={{ __html: mbDesc.replace(/\n/g, "<br>") }} />
              </div>
              <div className="g7-guar-seal">{money_back.money_back_image ? <img src={money_back.money_back_image} alt="Guarantee" /> : <GuaranteeSeal />}</div>
            </div>
          </div>
        </section>
      ) : null}

      {/* FAQ */}
      {faq?.status && faq?.list?.length ? (
        <section className="g7-sec" id="faq">
          <div className="g7-wrap g7-faq-wrap">
            <div className="g7-head g7-head-center rv"><span className="g7-eyebrow">FAQ</span><h2>{faq.heading || "Frequently asked questions"}</h2></div>
            <div className="g7-faq rv">
              {faq.list.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div className={`g7-fitem${open ? " open" : ""}`} key={i}>
                    <button className="g7-fq" type="button" onClick={() => setOpenFaq(open ? null : i)} aria-expanded={open}><span>{f.question || ""}</span><I.plus className="g7-fic" /></button>
                    {open ? <div className="g7-fa">{f.answer || ""}</div> : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* CTA */}
      <section className="g7-sec">
        <div className="g7-wrap">
          <div className="g7-cta rv">
            <div className="g7-cta-glow" aria-hidden="true" />
            <h2><span className="g7-grad">{heroTitle}</span></h2>
            {heroSub ? <p>{heroSub}</p> : null}
            <a href={enrollLink} className="g7-btn g7-btn-p g7-btn-lg g7-shimmer"><I.cap /> {enrollText} <I.arrow /></a>
            <div className="g7-cta-note"><I.shield /><span>{lbl("money_back_guarantee", "30-Day money-back guarantee.")} {lbl("not_satisfied", "Not satisfied? Full refund, no questions asked.")}</span></div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="g7-foot">
        <div className="g7-wrap g7-foot-in">
          <div className="g7-foot-social">
            {social.x_url ? <a href={social.x_url} target="_blank" rel="noopener noreferrer"><I.x /></a> : null}
            {social.youtube_url ? <a href={social.youtube_url} target="_blank" rel="noopener noreferrer"><I.youtube /></a> : null}
            {social.linkedin_url ? <a href={social.linkedin_url} target="_blank" rel="noopener noreferrer"><I.linkedin /></a> : null}
            {social.instagram_url ? <a href={social.instagram_url} target="_blank" rel="noopener noreferrer"><I.instagram /></a> : null}
            {social.facebook_url ? <a href={social.facebook_url} target="_blank" rel="noopener noreferrer"><I.facebook /></a> : null}
          </div>
          {hasPrivacy || hasTerms ? (
            <div className="g7-foot-links">
              {hasPrivacy ? <button type="button" onClick={() => setOpenPolicy("privacy")}>{policies.privacy_subject || "Privacy Policy"}</button> : null}
              {hasTerms ? <button type="button" onClick={() => setOpenPolicy("terms")}>{policies.terms_subject || "Terms"}</button> : null}
            </div>
          ) : null}
        </div>
      </footer>

      <PolicyModal open={openPolicy} policies={policies} onClose={() => setOpenPolicy(null)} />
    </div>
  );
}
